import { api } from "../../app/api";
import { commandMsg } from "../../app/commandMsg";
import { queryMsg } from "../../app/queryMsg";
import { MessageResponse } from "../../common/types/apiTypes";
import { Options } from "../../common/types/requestOptions";
import {
  AssignConversation,
  AssignConversationResponse,
  CreateConversation,
  ConversationAttachmentDownloadUrl,
  ConversationAttachmentDownloadUrlParams,
  ConversationAttachmentUploadUrl,
  ConversationAttachmentUploadUrlParams,
  CreateConversationMessage,
  CreateConversationMessageResponse,
  CreateConversationResponse,
  MarkConversationMessagesRead,
  MarkConversationMessagesReadResponse,
  TransferConversation,
  UpdateConversationStatus,
} from "./types/commands";
import {
  InboxSummary,
  ListConversationMessagesFilters,
  ListConversationMessagesResponse,
  ListConversations,
  ListConversationsFilters,
  ListTeams,
  ListTopics,
  SearchConversationsFilters,
} from "./types/queries";

export const getInboxSummary = async (
  _params: void,
  options: Options = {},
): Promise<MessageResponse<InboxSummary>> =>
  queryMsg<Record<string, never>, InboxSummary>(
    "/messaging/qry/getInboxSummary",
    {},
    options,
  );

export const listTeams = async (
  params: { limit?: number; nextToken?: string } = {},
  options: Options = {},
): Promise<MessageResponse<ListTeams>> =>
  queryMsg<{ limit?: number; nextToken?: string }, ListTeams>(
    "/messaging/qry/listTeams",
    params,
    options,
  );

export const listTopics = async (
  _params: void,
  options: Options = {},
): Promise<MessageResponse<ListTopics>> =>
  queryMsg<Record<string, never>, ListTopics>(
    "/messaging/qry/listTopics",
    {},
    options,
  );

export const listConversations = async (
  params: ListConversationsFilters | SearchConversationsFilters,
  options: Options = {},
): Promise<MessageResponse<ListConversations>> =>
  queryMsg<ListConversationsFilters | SearchConversationsFilters, ListConversations>(
    "/messaging/qry/listConversations",
    params,
    options,
  );

export const createConversation = async (
  body: CreateConversation,
  options: Options = {},
): Promise<MessageResponse<CreateConversationResponse>> =>
  commandMsg<CreateConversation, CreateConversationResponse>(
    "/messaging/cmd/createConversation",
    body,
    options,
  );

export const createConversationMessage = async (
  body: CreateConversationMessage,
  options: Options = {},
): Promise<MessageResponse<CreateConversationMessageResponse>> =>
  commandMsg<CreateConversationMessage, CreateConversationMessageResponse>(
    "/messaging/cmd/createConversationMessage",
    body,
    options,
  );

export const updateConversationStatus = async (
  body: UpdateConversationStatus,
  options: Options = {},
): Promise<MessageResponse<any>> =>
  commandMsg<UpdateConversationStatus, any>(
    "/messaging/cmd/updateConversationStatus",
    body,
    options,
  );

export const transferConversation = async (
  body: TransferConversation,
  options: Options = {},
): Promise<MessageResponse<Record<string, never>>> =>
  commandMsg<TransferConversation, Record<string, never>>(
    "/messaging/cmd/transferConversation",
    body,
    options,
  );

export const assignConversation = async (
  body: AssignConversation,
  options: Options = {},
): Promise<MessageResponse<AssignConversationResponse>> =>
  commandMsg<AssignConversation, AssignConversationResponse>(
    "/messaging/cmd/assignConversation",
    body,
    options,
  );

export const listConversationMessages = async (
  params: ListConversationMessagesFilters, // conversationI
  options: Options = {},
): Promise<MessageResponse<ListConversationMessagesResponse>> =>
  queryMsg<ListConversationMessagesFilters, ListConversationMessagesResponse>(
    "/messaging/qry/listConversationMessages",
    params,
    options,
  );

export const getConversationAttachmentUploadUrl = async (
  params: ConversationAttachmentUploadUrlParams,
  options: Options = {},
): Promise<ConversationAttachmentUploadUrl> => {
  const response = await queryMsg<
    ConversationAttachmentUploadUrlParams,
    ConversationAttachmentUploadUrl
  >("/messaging/qry/conversationAttachmentUploadUrl", params, options);
  return response.result;
};

export const getConversationAttachmentDownloadUrl = async (
  params: ConversationAttachmentDownloadUrlParams,
  options: Options = {},
): Promise<ConversationAttachmentDownloadUrl> => {
  const response = await queryMsg<
    ConversationAttachmentDownloadUrlParams,
    ConversationAttachmentDownloadUrl
  >("/messaging/qry/conversationAttachmentDownloadUrl", params, options);
  return response.result;
};

export const uploadConversationAttachmentToS3 = (
  url: string,
  file: File,
  contentType: string,
): Promise<unknown> =>
  api.upload(url, {
    headers: {
      "Content-Type": contentType,
      "Content-Length": String(file.size),
    },
    body: file,
  });

export const markConversationMessagesRead = async (
  body: MarkConversationMessagesRead,
  options: Options = {},
): Promise<MessageResponse<MarkConversationMessagesReadResponse>> =>
  commandMsg<
    MarkConversationMessagesRead,
    MarkConversationMessagesReadResponse
  >("/messaging/cmd/markConversationMessagesRead", body, options);
